import React, { type FC, memo, use, useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Label, Separator, Switch } from "@heroui/react";

import type { ProgramSettings } from "@domain/models/ProgramSettings";
import { CompanyAccountingYearContext, CompanyContext, CompanyControlContext } from "@domain/context/CompanyContext";
import { GQL } from "@binale-tech/shared";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import { type ProductKey } from "@domain/models/Product";
import { ProductFactory } from "@domain/core/ProductFactory";

type Props = {
    pk: ProductKey;
};

const AnsichtViewImpl: FC<Props> = ({ pk }) => {
    const { programSettingsProvider, companyGQL } = use(CompanyContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { updateProductSettings } = use(CompanyControlContext);
    const settings = useMemo(() => programSettingsProvider(pk), [pk, programSettingsProvider]);
    const config = ProductFactory.getProductConfig(pk as GQL.IProductKey);

    const onSaveSettings = useCallback(
        (name: keyof ProgramSettings) => (value: boolean) => {
            updateProductSettings(pk, { [name]: value });
        },
        [pk, updateProductSettings],
    );
    const hasContactsAccess = ProductAccessUtils.hasCompanyProductAccess(GQL.IProductKey.Contacts, companyGQL);

    const handleHideContactsChange = useCallback(
        (value: boolean) => onSaveSettings("hideContacts")(!value),
        [onSaveSettings],
    );
    const handleHideBuchTextChange = useCallback(
        (value: boolean) => onSaveSettings("hideBuchText")(!value),
        [onSaveSettings],
    );

    return (
        <div className="flex flex-col gap-4 my-4 relative">
            <div className="flex w-full gap-2 items-center">
                <h4 className="text-base font-bold text-nowrap">
                    <FormattedMessage id="app.titles.settings.formSettings" />
                </h4>
            </div>
            <div className="flex gap-4">
                {hasContactsAccess && (
                    <div data-testid="hideContacts">
                        <Switch isSelected={!settings.hideContacts} onChange={handleHideContactsChange}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.titles.Contacts" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                )}
                {yearConfig?.taxation !== GQL.ICompanyTaxation.Kust ? (
                    <div data-testid="useExpertBu">
                        <Switch isSelected={settings.useExpertBu} onChange={onSaveSettings("useExpertBu")}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.accounting.buInsteadOfUst" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                ) : undefined}
                <div data-testid="useCurrency">
                    <Switch isSelected={settings.useCurrency} onChange={onSaveSettings("useCurrency")}>
                        <Switch.Content>
                            <Switch.Control>
                                <Switch.Thumb />
                            </Switch.Control>
                            <Label>
                                <FormattedMessage id="app.accounting.useCurrency" />
                            </Label>
                        </Switch.Content>
                    </Switch>
                </div>
            </div>
            <div className="flex w-full gap-2 items-center">
                <h4 className="text-base font-bold text-nowrap">
                    <FormattedMessage id="app.titles.settings.optionalFields" />
                </h4>
                <Separator className="shrink" />
            </div>

            <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                    <div data-testid="useKs">
                        <Switch isSelected={settings.useKs} onChange={onSaveSettings("useKs")}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.titles.tag" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                    <div data-testid="hideBuchText">
                        <Switch isSelected={!settings.hideBuchText} onChange={handleHideBuchTextChange}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.fields.buchtext" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                </div>
                <div className="flex gap-4">
                    <div data-testid="useBuchText2">
                        <Switch isSelected={settings.useBuchText2} onChange={onSaveSettings("useBuchText2")}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.fields.buchtext2" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                    <div data-testid="useBelegfeld2">
                        <Switch isSelected={settings.useBelegfeld2} onChange={onSaveSettings("useBelegfeld2")}>
                            <Switch.Content>
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                                <Label>
                                    <FormattedMessage id="app.fields.internal_num" />
                                </Label>
                            </Switch.Content>
                        </Switch>
                    </div>
                    {config.useFalligkeit && (
                        <div data-testid="useDueDate">
                            <Switch isSelected={settings.useDueDate} onChange={onSaveSettings("useDueDate")}>
                                <Switch.Content>
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                    <Label>
                                        <FormattedMessage id="app.fields.falligkeit" />
                                    </Label>
                                </Switch.Content>
                            </Switch>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex w-full gap-2 items-center">
                <h4 className="text-base font-bold text-nowrap">
                    <FormattedMessage id="app.accounting.moreFields" />
                </h4>
                <Separator className="shrink" />
            </div>

            <div className="flex gap-4">
                <div data-testid="useVatNumber">
                    <Switch isSelected={settings.useVatNumber} onChange={onSaveSettings("useVatNumber")}>
                        <Switch.Content>
                            <Switch.Control>
                                <Switch.Thumb />
                            </Switch.Control>
                            <Label>
                                <FormattedMessage id="app.fields.vatNb" />
                            </Label>
                        </Switch.Content>
                    </Switch>
                </div>
            </div>
        </div>
    );
};

export const AnsichtView = memo(AnsichtViewImpl);
